import React, { useState, useEffect } from "react";
import { getAdminEmails } from "../../../api/services/services";
import { Link, useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Badge
} from "@mui/material";
// Icons
import DashboardIcon from '@mui/icons-material/DashboardRounded';
import LocalShippingIcon from '@mui/icons-material/LocalShippingRounded';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteRounded';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import DescriptionIcon from '@mui/icons-material/DescriptionRounded';
import DirectionsCarRoundedIcon from '@mui/icons-material/DirectionsCarRounded';
import MenuOpenIcon from '@mui/icons-material/MenuOpenRounded';
import MenuIcon from '@mui/icons-material/MenuRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import { getAdminUnreadTotal } from "../../../api/services/communicationServices";

import "./style.css";
import logo from "../../../imgs/logo-final.png";
import BrandingIcon from "../../../imgs/branding-icon.png";

const FloatingSidebar = () => {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(
    localStorage.getItem("isSideBarOpen") === "true"
  );

  const toggleSidebar = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    localStorage.setItem("isSideBarOpen", newState);
    // Dispatch custom event if other components (like Header) need to know
    window.dispatchEvent(new Event("sidebarChange"));
  };

  const [adminEmails, setAdminEmails] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem("shelUserData"));
  const currentUserEmail = currentUser?.email;

  useEffect(() => {
    getAdminEmails()
      .then((res) => {
        setAdminEmails(res?.data?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch admin emails", err);
      });

    const fetchUnread = () => {
      getAdminUnreadTotal()
        .then((res) => {
          const val = res?.data?.data;
          const count = typeof val === "object" && val !== null ? (val?.total || 0) : (Number(val) || 0);
          setUnreadCount(count);
        })
        .catch(() => {});
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 10000);
    return () => clearInterval(interval);
  }, []);

  const isAdmin = adminEmails.includes(currentUserEmail);

  const menuItems = [
    {
      text: "Surveys / Shipments",
      icon: <DashboardIcon />,
      path: "/",
      activePaths: ["/", "/surveys", "/survey", "/survey-preview"],
    },
    {
      text: "Recycle Bin",
      icon: <DeleteSweepRoundedIcon />,
      path: "/recycle-bin",
      activePaths: ["/recycle-bin"],
    },
    {
      text: "Communications",
      icon: <ForumRoundedIcon />,
      path: "/communication",
      activePaths: ["/communication"],
      badge: unreadCount,
    },
    {
      text: "Request Shipment",
      icon: <LocalShippingIcon />,
      path: "/survey-request",
      activePaths: ["/survey-request"],
    },
    {
      text: "Quotations",
      icon: <RequestQuoteIcon />,
      path: "/quotations",
      activePaths: ["/quotations"],
      disabled: true, // Based on commented out code in old sidebar
    },
    {
      text: "Role Management",
      icon: <AdminPanelSettingsIcon />,
      path: "/role-management",
      activePaths: ["/role-management"],
      adminOnly: true,
    },
    {
      text: "Car & Media Gallery",
      icon: <DirectionsCarRoundedIcon />,
      path: "/cars",
      activePaths: ["/cars", "/car-media"],
    },
    {
      text: "Prebid Agreements",
      icon: <DescriptionIcon />,
      path: "/prebid-agreement-list",
      activePaths: [
        "/prebid-agreement-list",
        "/create-update-prebid",
        "/prebid-client-preview",
      ],
    },
  ];

  const isActive = (item) => {
    if (item.activePaths) {
      const currentPath = location.pathname;
      return item.activePaths.some(path => 
        path === "/" ? currentPath === "/" : currentPath.startsWith(path)
      );
    }
    return location.pathname === item.path;
  };

  return (
    <div className={`floating-sidebar no-print ${isOpen ? "expanded" : "collapsed"}`}>
      <div className="glass-panel sidebar-content">
        {/* Header / Logo */}
        <div className="sidebar-header">
           <div className="logo-container">
             {isOpen ? (
                 <img src={logo} alt="Shelozon" className="sidebar-logo-img" />
             ) : (
                <img src={BrandingIcon} alt="S" className="sidebar-logo-mini" />
             )}
           </div>
           <IconButton onClick={toggleSidebar} className="toggle-btn">
             {isOpen ? <MenuOpenIcon /> : <MenuIcon />}
           </IconButton>
        </div>

        {/* Menu List */}
        <List className="sidebar-list">
          {menuItems.map((item, index) => {
            if (item.disabled) return null;
            if (item.adminOnly && !isAdmin) return null;

            const active = isActive(item);
             
             return (
               <Tooltip title={!isOpen ? item.text : ""} placement="right" arrow key={index}>
                 <ListItem
                   button
                   component={Link}
                   to={item.path}
                   className={`sidebar-item ${active ? "active" : ""}`}
                 >
                   <ListItemIcon className="item-icon">
                     {item.badge > 0 ? (
                       <Badge badgeContent={item.badge} color="error" max={99}>
                         {item.icon}
                       </Badge>
                     ) : (
                       item.icon
                     )}
                   </ListItemIcon>
                   {isOpen && (
                     <ListItemText 
                       primary={item.text} 
                       primaryTypographyProps={{ 
                         style: { fontWeight: active ? 600 : 400 } 
                       }}
                     />
                   )}
                   {active && isOpen && <div className="active-indicator" />}
                 </ListItem>
               </Tooltip>
             );
          })}
        </List>

        {/* Bottom Logout Action */}
        <div style={{ marginTop: "auto", paddingBottom: "10px" }}>
          <Tooltip title={!isOpen ? "Logout" : ""} placement="right" arrow>
            <ListItem
              button
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                window.location.href = "/";
              }}
              className="sidebar-item logout-item"
              style={{
                borderRadius: "10px",
                margin: "4px 8px",
                color: "#ef4444",
                transition: "all 0.2s",
              }}
            >
              <ListItemIcon className="item-icon" style={{ color: "#ef4444", minWidth: isOpen ? "40px" : "unset" }}>
                <LogoutRoundedIcon />
              </ListItemIcon>
              {isOpen && (
                <ListItemText
                  primary="Logout"
                  primaryTypographyProps={{
                    style: { fontWeight: 600, color: "#ef4444", fontSize: "14px" },
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default FloatingSidebar;
